/**
 * app/(marketing)/pricing.tsx - getabeam.com pricing + account creation (web, public).
 *
 * Self-serve plans collect an email and start a Stripe Checkout subscription via the public
 * backend endpoint (src/api/public.startCheckout). On success the buyer is sent to Stripe;
 * after payment the backend provisions the org and emails a set-password link (→ /welcome).
 * The "Business" plan routes to sales by email instead.
 *
 * No raw design values here - visuals come from src/marketing/* + src/components/ui + tokens.
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { Text, Card, Field, Button } from '../../src/components/ui';
import { MarketingShell, useMarketingNav } from '../../src/marketing/shell';
import { Section, Eyebrow, PricingCards, FaqList } from '../../src/marketing/sections';
import { PRICING, type BillingInterval } from '../../src/marketing/content';
import { startCheckout } from '../../src/api/public';
import { openExternalUrl } from '../../src/platform/linking';
import { ApiRequestError } from '../../src/api/client';
import { theme } from '../../src/theme';

function CheckoutPanel({ planName, interval }: { planName: string; interval: BillingInterval }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    const e = email.trim();
    if (!e || !e.includes('@')) {
      setError('Enter a valid email to continue.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { url } = await startCheckout(e, interval);
      // Same-tab redirect to Stripe Checkout on web; new tab elsewhere.
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = url;
      } else {
        await openExternalUrl(url);
      }
    } catch (err) {
      setError(err instanceof ApiRequestError || err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  return (
    <Card style={styles.panel}>
      <Text variant="subhead">Set up your {planName} plan</Text>
      <Text variant="small" color="secondary">
        {interval === 'year' ? 'Billed annually · ' : ''}
        {PRICING.guarantee}
      </Text>
      <Field
        label="Work email"
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        textContentType="emailAddress"
        autoComplete="email"
        placeholder="you@yourcompany.com"
        editable={!loading}
        accessibilityLabel="Work email"
        error={error ?? undefined}
        onSubmitEditing={go}
        returnKeyType="go"
      />
      <Button title="Continue to secure checkout" onPress={go} loading={loading} disabled={loading} size="lg" />
      <Text variant="small" color="secondary">
        Payments are processed by Stripe. You can cancel anytime from your billing portal.
      </Text>
    </Card>
  );
}

function PricingBody() {
  const { navigate } = useMarketingNav();
  const { canceled } = useLocalSearchParams<{ canceled?: string }>();
  const [selected, setSelected] = useState<{ name: string; interval: BillingInterval } | null>(null);

  function onSelect(planId: string, selfServe: boolean, interval: BillingInterval) {
    if (!selfServe) {
      void openExternalUrl(`mailto:${PRICING.contactEmail}?subject=Beam%20Business%20plan`);
      return;
    }
    const plan = PRICING.plans.find((p) => p.id === planId);
    setSelected({ name: plan?.name ?? 'Beam', interval });
  }

  return (
    <View>
      <Section tone="paper">
        <Eyebrow>Pricing</Eyebrow>
        <Text variant="display">One plan. Everything included.</Text>
        <Text variant="subhead" color="secondary" style={styles.lede}>
          {PRICING.guarantee}
        </Text>

        {canceled ? (
          <Card style={styles.canceledNote}>
            <Text variant="bodyStrong">Checkout canceled</Text>
            <Text variant="small" color="secondary">
              No charge was made. Choose a plan below whenever you're ready.
            </Text>
          </Card>
        ) : null}

        <View style={styles.gap} />
        <PricingCards onSelect={onSelect} />

        {selected ? (
          <View style={styles.panelWrap}>
            <CheckoutPanel planName={selected.name} interval={selected.interval} />
          </View>
        ) : null}
      </Section>

      <Section tone="surface">
        <FaqList />
      </Section>

      <Section tone="paper">
        <View style={styles.center}>
          <Text variant="subhead" color="secondary">
            Already have an account?
          </Text>
          <View style={styles.signinBtn}>
            <Button title="Open the web app" variant="secondary" fullWidth={false} onPress={() => navigate('/login')} />
          </View>
        </View>
      </Section>
    </View>
  );
}

export default function PricingScreen() {
  return (
    <MarketingShell>
      <PricingBody />
    </MarketingShell>
  );
}

const styles = StyleSheet.create({
  lede: { marginTop: theme.spacing.sm, maxWidth: 560 },
  gap: { height: theme.spacing['2xl'] },
  panelWrap: { marginTop: theme.spacing['3xl'], maxWidth: 480 },
  panel: { gap: theme.spacing.md },
  canceledNote: { marginTop: theme.spacing.lg, maxWidth: 480, gap: theme.spacing.xs },
  center: { alignItems: 'center', gap: theme.spacing.md },
  signinBtn: { minWidth: 220 },
});
